import type { StudentReview } from "@/content/student-reviews";
import getAccessToken from "@/utils/google";

export const GOOGLE_BUSINESS_PROFILE_URL =
  "https://www.google.com/maps/place/Cosmetic+College/@51.3661557,0.0539251,641m/data=!3m1!1e3!4m8!3m7!1s0x47d8abe7c3630381:0x7b8f752d9d607441!8m2!3d51.3661557!4d0.0539251!9m1!1b1!16s%2Fg%2F%11pr92bwny";

/** Last verified fallback when GBP env/token/API is unavailable (15 Aug 2026). */
export const GOOGLE_BUSINESS_PROFILE_RATING_FALLBACK = 4.5;

/** On-site reviews page — trust pills link here, not to GBP. */
export const REVIEWS_PAGE_PATH = "/reviews/";

/** Last verified fallback when GBP env/token/API is unavailable (15 Aug 2026). */
export const GOOGLE_BUSINESS_PROFILE_REVIEW_COUNT_FALLBACK = 451;

export type GoogleBusinessProfileReviewStats = {
  averageRating: number;
  totalReviewCount: number;
};

type GoogleReview = {
  reviewer?: {
    displayName?: string;
  };
  starRating?: string;
  comment?: string;
  createTime?: string;
  updateTime?: string;
};

type GoogleReviewsResponse = {
  reviews?: GoogleReview[];
  averageRating?: number;
  totalReviewCount?: number;
  nextPageToken?: string;
};

export type LiveGoogleReviews = {
  reviews: StudentReview[];
  averageRating: number | null;
  totalReviewCount: number | null;
};

const STAR_RATING_VALUES: Record<string, number> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
};

const MINIMUM_PUBLIC_REVIEW_RATING = 4;
const MAX_REVIEW_PAGES = 100;

export function resolveGoogleBusinessProfileReviewStats(
  averageRating?: number | null,
  totalReviewCount?: number | null,
): GoogleBusinessProfileReviewStats {
  return {
    averageRating:
      typeof averageRating === "number" && Number.isFinite(averageRating)
        ? averageRating
        : GOOGLE_BUSINESS_PROFILE_RATING_FALLBACK,
    totalReviewCount:
      typeof totalReviewCount === "number" && Number.isFinite(totalReviewCount)
        ? Math.round(totalReviewCount)
        : GOOGLE_BUSINESS_PROFILE_REVIEW_COUNT_FALLBACK,
  };
}

export function formatGoogleBusinessProfileRatingLabel(
  stats: GoogleBusinessProfileReviewStats,
): string {
  return `${stats.averageRating.toFixed(1)} · ${stats.totalReviewCount} Google reviews`;
}

export function formatGoogleBusinessProfileTrustStripItem(
  stats: GoogleBusinessProfileReviewStats,
): string {
  return formatGoogleBusinessProfileRatingLabel(stats);
}

export function isGoogleReviewsTrustStripItem(item: string): boolean {
  return /google reviews/i.test(item.trim());
}

async function fetchGoogleBusinessProfileReviewsPage(
  locationName: string,
  accessToken: string,
  options: { pageSize: number; pageToken?: string },
): Promise<GoogleReviewsResponse> {
  const url = new URL(
    `https://mybusiness.googleapis.com/v4/${locationName}/reviews`,
  );
  url.searchParams.set("pageSize", String(options.pageSize));
  url.searchParams.set("orderBy", "updateTime desc");
  if (options.pageToken) {
    url.searchParams.set("pageToken", options.pageToken);
  }

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(
      `Google Business Profile reviews request failed with ${response.status}`,
    );
  }

  return (await response.json()) as GoogleReviewsResponse;
}

/**
 * Fetch aggregate GBP rating/count from the first API page only.
 * Falls back to 4.5 / 451 when env, token, or API is unavailable.
 */
export async function fetchGoogleBusinessProfileStats(): Promise<GoogleBusinessProfileReviewStats> {
  const locationName = process.env.NEXT_GOOGLE_BUSINESS_PROFILE_LOCATION?.trim();

  if (!locationName) {
    return resolveGoogleBusinessProfileReviewStats(null, null);
  }

  try {
    const accessToken = await getAccessToken();
    const data = await fetchGoogleBusinessProfileReviewsPage(
      locationName,
      accessToken,
      { pageSize: 1 },
    );

    return resolveGoogleBusinessProfileReviewStats(
      data.averageRating,
      data.totalReviewCount,
    );
  } catch (error) {
    console.warn("[gbp] Failed to fetch review stats:", error);
    return resolveGoogleBusinessProfileReviewStats(null, null);
  }
}

/**
 * Fetch reviews from the verified Google Business Profile location.
 *
 * The API is deliberately optional: without a Business Profile OAuth refresh
 * token and location resource name, the reviews page uses the curated review
 * set already used across the public site.
 */
export async function fetchGoogleBusinessProfileReviews(
  limit = 50,
): Promise<LiveGoogleReviews | null> {
  const locationName = process.env.NEXT_GOOGLE_BUSINESS_PROFILE_LOCATION?.trim();

  if (!locationName) {
    return null;
  }

  try {
    const accessToken = await getAccessToken();
    const reviews: StudentReview[] = [];
    const reviewLimit = Math.max(1, Math.min(limit, 5000));
    let pageToken = "";
    let averageRating: number | null = null;
    let totalReviewCount: number | null = null;

    for (let page = 0; page < MAX_REVIEW_PAGES; page += 1) {
      const data = await fetchGoogleBusinessProfileReviewsPage(
        locationName,
        accessToken,
        { pageSize: 50, pageToken: pageToken || undefined },
      );
      averageRating ??= data.averageRating ?? null;
      totalReviewCount ??= data.totalReviewCount ?? null;

      for (const review of data.reviews ?? []) {
        const body = review.comment?.trim();
        const author = review.reviewer?.displayName?.trim();
        const rating = review.starRating
          ? STAR_RATING_VALUES[review.starRating]
          : undefined;

        if (!body || !author || !rating || rating < MINIMUM_PUBLIC_REVIEW_RATING) {
          continue;
        }

        reviews.push({
          body,
          link: GOOGLE_BUSINESS_PROFILE_URL,
          author: { name: author },
          rating,
          publishedAt: review.updateTime || review.createTime,
          source: "google",
        });

        if (reviews.length >= reviewLimit) {
          break;
        }
      }

      if (reviews.length >= reviewLimit || !data.nextPageToken) {
        break;
      }

      pageToken = data.nextPageToken;
    }

    return {
      reviews: reviews.slice(0, reviewLimit),
      averageRating,
      totalReviewCount,
    };
  } catch (error) {
    console.warn("[gbp] Failed to fetch reviews:", error);
    return null;
  }
}
